package er.prateek.crossly.generation.riddle

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.riddle.Riddle
import er.prateek.crossly.data.model.riddle.RiddleKind
import er.prateek.crossly.data.model.riddle.RiddleSegment
import er.prateek.crossly.data.model.riddle.RiddleSet
import er.prateek.crossly.data.model.riddle.ShapeToken
import er.prateek.crossly.game.crossword.PuzzleRandom
import er.prateek.crossly.game.crossword.stableSeed

/**
 * Generates rapid-fire riddle sets at runtime: order-of-operations arithmetic, additive
 * shape-algebra, and number sequences. Deterministic per (levelId, index, nonce) so a set is
 * reproducible, varied per attempt via the nonce. Every answer is a non-negative integer so the
 * number pad is all the input a player needs, and every question is uniquely determined.
 */
object RiddleGenerator {

    /** Remote-config-driven overrides applied on top of each tier's defaults. */
    data class Options(
        val allowArithmetic: Boolean = true,
        val allowSequences: Boolean = true,
        val allowShapeAlgebra: Boolean = true,
        val allowMultiply: Boolean = true,
        val allowDivide: Boolean = true,
        /** Find-the-missing-number, word problems, think-of-a-number, grid rules and emoji algebra. */
        val allowLogic: Boolean = true,
    )

    private val PLUS = "+"
    private val MINUS = "−"
    private val TIMES = "×"
    private val DIVIDE = "÷"
    private val MUL_DIV = setOf(TIMES, DIVIDE)

    private val ALL_KINDS = RiddleKind.entries

    private data class TierSpec(
        val kinds: List<RiddleKind>,
        val arithmeticOperands: IntRange,
        /** Fraction of the tier (0..1) a player must climb before × and ÷ appear. */
        val mulDivFromProgress: Double,
        /** Largest operand next to a × or ÷ (kept small so products stay mental-math sized). */
        val maxOperand: Int,
        /** Largest operand for plain + / − terms; grows to 2 and 3 digits in the higher tiers. */
        val maxAddOperand: Int,
        val shapeCount: Int,
        val shapeMaxValue: Int,
        val sequenceMaxStart: Int,
        val sequenceMaxStep: Int,
        val sequenceMaxRatio: Int,
        val useMulDiv: Boolean = false,
    )

    private fun specFor(tier: DifficultyTier): TierSpec = when (tier) {
        DifficultyTier.EASY -> TierSpec(
            kinds = ALL_KINDS,
            arithmeticOperands = 2..2, mulDivFromProgress = 0.5, maxOperand = 9, maxAddOperand = 20,
            shapeCount = 2, shapeMaxValue = 12, sequenceMaxStart = 9, sequenceMaxStep = 5, sequenceMaxRatio = 2,
        )
        DifficultyTier.MEDIUM -> TierSpec(
            kinds = ALL_KINDS,
            arithmeticOperands = 3..3, mulDivFromProgress = 0.0, maxOperand = 12, maxAddOperand = 99,
            shapeCount = 2, shapeMaxValue = 20, sequenceMaxStart = 12, sequenceMaxStep = 9, sequenceMaxRatio = 3,
        )
        DifficultyTier.HARD -> TierSpec(
            kinds = ALL_KINDS,
            arithmeticOperands = 3..4, mulDivFromProgress = 0.0, maxOperand = 15, maxAddOperand = 999,
            shapeCount = 3, shapeMaxValue = 25, sequenceMaxStart = 15, sequenceMaxStep = 12, sequenceMaxRatio = 3,
        )
        DifficultyTier.EXPERT -> TierSpec(
            kinds = ALL_KINDS,
            arithmeticOperands = 4..4, mulDivFromProgress = 0.0, maxOperand = 20, maxAddOperand = 999,
            shapeCount = 3, shapeMaxValue = 40, sequenceMaxStart = 20, sequenceMaxStep = 15, sequenceMaxRatio = 4,
        )
    }

    fun generateSet(
        levelId: String,
        tier: DifficultyTier,
        count: Int,
        nonce: Long,
        options: Options = Options(),
        levelIndex: Int = 1,
        levelsPerTier: Int = 30,
    ): RiddleSet {
        // Difficulty ramps within the tier: numbers and the arithmetic operand count grow as the
        // level index climbs, so set 1 is gentle and set 30 is noticeably harder.
        val span = (levelsPerTier - 1).coerceAtLeast(1)
        val p = (levelIndex - 1).coerceIn(0, span).toDouble() / span
        val spec = scaleForLevel(specFor(tier), p)
        val riddles = ArrayList<Riddle>(count)
        for (q in 0 until count) {
            val seed = "$levelId#$q".stableSeed() + nonce
            // Never show the same kind twice in a row, so a run doesn't feel like one repeated question.
            riddles += generateRiddle("$levelId-q$q", spec, options, PuzzleRandom(seed), avoid = riddles.lastOrNull()?.kind)
        }
        return RiddleSet(id = levelId, tier = tier, riddles = riddles)
    }

    private fun lerpInt(from: Int, to: Int, t: Double): Int = (from + (to - from) * t).toInt()

    /** Shrinks a tier's number ranges for early levels and opens them up fully by the last level. */
    private fun scaleForLevel(spec: TierSpec, p: Double): TierSpec = spec.copy(
        arithmeticOperands = lerpInt(spec.arithmeticOperands.first, spec.arithmeticOperands.last, p)..spec.arithmeticOperands.last,
        useMulDiv = p >= spec.mulDivFromProgress,
        maxOperand = lerpInt((spec.maxOperand / 2).coerceAtLeast(5), spec.maxOperand, p),
        maxAddOperand = lerpInt((spec.maxAddOperand / 2).coerceAtLeast(9), spec.maxAddOperand, p),
        shapeMaxValue = lerpInt((spec.shapeMaxValue / 2).coerceAtLeast(5), spec.shapeMaxValue, p),
        sequenceMaxStart = lerpInt((spec.sequenceMaxStart / 2).coerceAtLeast(3), spec.sequenceMaxStart, p),
        sequenceMaxStep = lerpInt((spec.sequenceMaxStep / 2).coerceAtLeast(2), spec.sequenceMaxStep, p),
    )

    private fun generateRiddle(id: String, spec: TierSpec, options: Options, rng: PuzzleRandom, avoid: RiddleKind?): Riddle {
        val allowed = spec.kinds.filter {
            when (it) {
                RiddleKind.ARITHMETIC -> options.allowArithmetic
                RiddleKind.SEQUENCE -> options.allowSequences
                RiddleKind.SHAPE_ALGEBRA -> options.allowShapeAlgebra
                else -> options.allowLogic
            }
        }.ifEmpty { listOf(RiddleKind.ARITHMETIC) }
        val kinds = allowed.filter { it != avoid }.ifEmpty { allowed }
        return when (kinds[rng.nextInt(kinds.size)]) {
            RiddleKind.ARITHMETIC -> arithmetic(id, spec, options, rng)
            RiddleKind.SHAPE_ALGEBRA -> shapeAlgebra(id, spec, rng)
            RiddleKind.SEQUENCE -> sequence(id, spec, rng)
            RiddleKind.ALGEBRA -> algebra(id, spec, rng)
            RiddleKind.WORD_PROBLEM -> wordProblem(id, spec, rng)
            RiddleKind.THINK_OF_NUMBER -> thinkOfNumber(id, spec, rng)
            RiddleKind.GRID_RULE -> gridRule(id, spec, rng)
            RiddleKind.EMOJI_ALGEBRA -> emojiAlgebra(id, spec, rng)
        }
    }

    private fun textRiddle(id: String, kind: RiddleKind, answer: Int, vararg lines: String): Riddle =
        Riddle(id, kind, lines.map { listOf(RiddleSegment.Text(it)) }, answer)

    // ---- Find the missing number ----

    private fun algebra(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        val x = rng.intIn(2, spec.maxAddOperand)
        val k = rng.intIn(2, spec.maxAddOperand)
        val m = rng.intIn(2, spec.maxOperand)
        val xm = x.coerceAtMost(30) // keeps × / ÷ answers mental-math sized
        val (line, answer) = when (rng.nextInt(6)) {
            0 -> "? $PLUS $k = ${x + k}" to x
            1 -> if (x >= k) "? $MINUS $k = ${x - k}" to x else "$k $PLUS ? = ${x + k}" to x
            2 -> "${x + k} $MINUS ? = $k" to x
            3 -> "? $TIMES $m = ${xm * m}" to xm
            4 -> "${xm * m} $DIVIDE ? = $m" to xm
            else -> "$m $TIMES ? $PLUS $k = ${m * xm + k}" to xm
        }
        return textRiddle(id, RiddleKind.ALGEBRA, answer, "Find the missing number", line)
    }

    // ---- Short story problems ----

    private val NAMES = listOf("Mia", "Leo", "Zara", "Kai", "Nova", "Ravi", "Luna", "Ezra", "Aria", "Dev")
    private val THINGS = listOf("stickers", "cards", "coins", "marbles", "cookies", "stars", "pens", "balloons")

    private fun wordProblem(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        val name = NAMES[rng.nextInt(NAMES.size)]
        val things = THINGS[rng.nextInt(THINGS.size)]
        val a = rng.intIn(5, spec.maxAddOperand.coerceAtLeast(12))
        val b = rng.intIn(2, spec.maxOperand)
        val c = rng.intIn(2, spec.maxOperand)
        return when (rng.nextInt(5)) {
            0 -> textRiddle(id, RiddleKind.WORD_PROBLEM, a + b * c, "$name has $a $things.", "Then buys $b packs of $c.", "How many now?")
            1 -> {
                val start = a + b
                textRiddle(id, RiddleKind.WORD_PROBLEM, start - b + c, "$name has $start $things.", "Loses $b, then finds $c.", "How many now?")
            }
            2 -> textRiddle(id, RiddleKind.WORD_PROBLEM, b * c, "1 ticket costs $b coins.", "$name buys $c tickets.", "Total coins?")
            3 -> textRiddle(id, RiddleKind.WORD_PROBLEM, c, "${b * c} $things shared equally", "between $b friends.", "How many each?")
            else -> textRiddle(id, RiddleKind.WORD_PROBLEM, a * b + c, "A bike rides $a km each hour", "for $b hours, then $c km more.", "Total km?")
        }
    }

    // ---- "I'm thinking of a number" ----

    private fun thinkOfNumber(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        val x = rng.intIn(2, spec.maxOperand.coerceAtLeast(6))
        var value = x
        val steps = mutableListOf<String>()
        val count = if (spec.arithmeticOperands.last >= 3) 2 else 1
        repeat(count) {
            when (rng.nextInt(4)) {
                0 -> { value *= 2; steps += "double it" }
                1 -> { value *= 3; steps += "triple it" }
                2 -> { val k = rng.intIn(2, 20); value += k; steps += "add $k" }
                else -> {
                    val k = rng.intIn(1, value.coerceAtLeast(2) - 1)
                    value -= k; steps += "subtract $k"
                }
            }
        }
        return textRiddle(id, RiddleKind.THINK_OF_NUMBER, x, "I think of a number.", "I ${steps.joinToString(", then ")}.", "I get $value. My number?")
    }

    // ---- Hidden rule in a little table ----

    private val RULES: List<(Int, Int) -> Int> = listOf(
        { a, b -> a * b },
        { a, b -> a + 2 * b },
        { a, b -> 2 * a + b },
        { a, b -> a * b + a },
        { a, b -> a * a - b },
    )

    private fun gridRule(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        val hi = (spec.maxOperand / 2).coerceIn(5, 9)
        repeat(100) {
            val ruleIndex = rng.nextInt(RULES.size)
            val rule = RULES[ruleIndex]
            val pairs = List(3) { rng.intIn(3, hi) to rng.intIn(2, hi) }
            if (pairs.any { (a, b) -> rule(a, b) < 0 }) return@repeat
            // Asking about a row already shown (or its mirror, for the symmetric rules) prints the
            // answer right above the question.
            val asked = pairs[2]
            if (pairs.take(2).any { it == asked || it == asked.second to asked.first }) return@repeat
            // Reject if any other rule fits the two shown rows: the answer must be uniquely deducible.
            val ambiguous = RULES.indices.any { i ->
                i != ruleIndex && pairs.take(2).all { (a, b) -> RULES[i](a, b) == rule(a, b) } &&
                    RULES[i](pairs[2].first, pairs[2].second) != rule(pairs[2].first, pairs[2].second)
            }
            if (ambiguous) return@repeat
            val lines = pairs.take(2).map { (a, b) -> ruleLine(a, b, rule(a, b).toString()) } +
                listOf(ruleLine(pairs[2].first, pairs[2].second, "?"))
            return Riddle(id, RiddleKind.GRID_RULE, listOf(RULE_TITLE) + lines, rule(pairs[2].first, pairs[2].second))
        }
        return Riddle(
            id, RiddleKind.GRID_RULE,
            listOf(RULE_TITLE, ruleLine(2, 3, "6"), ruleLine(3, 4, "12"), ruleLine(4, 5, "?")),
            20,
        )
    }

    private val RULE_TITLE = listOf(RiddleSegment.Text("Crack the "), RiddleSegment.Shape(ShapeToken.DIAMOND), RiddleSegment.Text(" rule"))

    private fun ruleLine(a: Int, b: Int, result: String): List<RiddleSegment> =
        listOf(RiddleSegment.Text("$a "), RiddleSegment.Shape(ShapeToken.DIAMOND), RiddleSegment.Text(" $b = $result"))

    // ---- Fruit algebra, drawn with the game's own icons ----

    /** Six, as the emoji fruits were, so `shuffled` draws the same numbers and a seed keeps its answers. */
    private val FRUITS = listOf(ShapeToken.STAR, ShapeToken.HEART, ShapeToken.FLAME, ShapeToken.BELL, ShapeToken.BULB, ShapeToken.TROPHY)

    private fun emojiAlgebra(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        val n = if (spec.shapeCount >= 3) 3 else 2
        val fruits = rng.shuffled(FRUITS).take(n)
        val cap = (spec.shapeMaxValue / 2).coerceAtLeast(6)
        val v = List(n) { rng.intIn(1, cap) }
        val lines = mutableListOf(sumLine(fruits[0], fruits[0], fruits[0], "${3 * v[0]}"))
        for (i in 1 until n) lines += sumLine(fruits[i - 1], fruits[i], fruits[i], "${v[i - 1] + 2 * v[i]}")
        lines += sumLine(fruits[n - 1], fruits[n - 1], fruits[0], "?")
        return Riddle(id, RiddleKind.EMOJI_ALGEBRA, lines, 2 * v[n - 1] + v[0])
    }

    private fun sumLine(a: ShapeToken, b: ShapeToken, c: ShapeToken, result: String): List<RiddleSegment> = listOf(
        RiddleSegment.Shape(a), RiddleSegment.Text(" $PLUS "), RiddleSegment.Shape(b), RiddleSegment.Text(" $PLUS "),
        RiddleSegment.Shape(c), RiddleSegment.Text(" = $result"),
    )

    private fun PuzzleRandom.intIn(lo: Int, hi: Int): Int = if (hi <= lo) lo else lo + nextInt(hi - lo + 1)

    // ---- Arithmetic (operator precedence) ----

    private fun arithmetic(id: String, spec: TierSpec, options: Options, rng: PuzzleRandom): Riddle {
        val ops = buildList {
            add(PLUS); add(MINUS)
            if (spec.useMulDiv && options.allowMultiply) add(TIMES)
            if (spec.useMulDiv && options.allowDivide) add(DIVIDE)
        }
        repeat(200) {
            val n = rng.intIn(spec.arithmeticOperands.first, spec.arithmeticOperands.last)
            val chosen = MutableList(n - 1) { ops[rng.nextInt(ops.size)] }
            // Operands touching a × or ÷ stay small; the rest of the + / − terms use the wider range.
            val operands = MutableList(n) { i ->
                val nearMulDiv = (i > 0 && chosen[i - 1] in MUL_DIV) || (i < chosen.size && chosen[i] in MUL_DIV)
                rng.intIn(1, if (nearMulDiv) spec.maxOperand else spec.maxAddOperand)
            }
            // For division, make the left operand a clean multiple so the result is an integer.
            for (i in chosen.indices) {
                if (chosen[i] == DIVIDE) {
                    val divisor = rng.intIn(2, spec.maxOperand)
                    operands[i + 1] = divisor
                    operands[i] = divisor * rng.intIn(1, spec.maxOperand)
                }
            }
            val value = evaluate(operands, chosen) ?: return@repeat
            if (value in 0..9999) {
                val text = buildString {
                    append(operands[0])
                    for (i in chosen.indices) { append(' '); append(chosen[i]); append(' '); append(operands[i + 1]) }
                    append(" = ?")
                }
                return Riddle(id, RiddleKind.ARITHMETIC, listOf(listOf(RiddleSegment.Text(text))), value)
            }
        }
        // Fallback: a trivial addition can never fail.
        val a = rng.intIn(1, spec.maxAddOperand); val b = rng.intIn(1, spec.maxAddOperand)
        return Riddle(id, RiddleKind.ARITHMETIC, listOf(listOf(RiddleSegment.Text("$a $PLUS $b = ?"))), a + b)
    }

    /** Evaluates with ×/÷ binding tighter than +/−, left-to-right; null if a division isn't exact. */
    private fun evaluate(operands: List<Int>, ops: List<String>): Int? {
        var acc = operands[0]
        var sign = 1
        var total = 0
        for (i in ops.indices) {
            when (ops[i]) {
                TIMES -> acc *= operands[i + 1]
                DIVIDE -> { val d = operands[i + 1]; if (d == 0 || acc % d != 0) return null; acc /= d }
                PLUS -> { total += sign * acc; acc = operands[i + 1]; sign = 1 }
                MINUS -> { total += sign * acc; acc = operands[i + 1]; sign = -1 }
            }
        }
        total += sign * acc
        return total
    }

    // ---- Shape algebra (additive chain, uniquely solvable by substitution) ----

    private fun shapeAlgebra(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        val shapes = listOf(ShapeToken.SQUARE, ShapeToken.CIRCLE, ShapeToken.TRIANGLE).take(spec.shapeCount)
        val values = shapes.map { rng.intIn(1, spec.shapeMaxValue) }
        val lines = mutableListOf<List<RiddleSegment>>()
        // First line reveals shape 0: □ + □ = 2*v0
        lines += listOf(
            RiddleSegment.Shape(shapes[0]), RiddleSegment.Text(" $PLUS "), RiddleSegment.Shape(shapes[0]),
            RiddleSegment.Text(" = ${2 * values[0]}"),
        )
        // Each subsequent line pairs the previous (now-known) shape with the next.
        for (i in 1 until shapes.size) {
            lines += listOf(
                RiddleSegment.Shape(shapes[i - 1]), RiddleSegment.Text(" $PLUS "), RiddleSegment.Shape(shapes[i]),
                RiddleSegment.Text(" = ${values[i - 1] + values[i]}"),
            )
        }
        // Ask for the last shape.
        lines += listOf(RiddleSegment.Shape(shapes.last()), RiddleSegment.Text(" = ?"))
        return Riddle(id, RiddleKind.SHAPE_ALGEBRA, lines, values.last())
    }

    // ---- Sequences ----

    private fun sequence(id: String, spec: TierSpec, rng: PuzzleRandom): Riddle {
        // Four terms can fit two rules with different next terms -- 2, 3, 5, 8 is both Fibonacci
        // (13) and growing gaps (12) -- and then a right answer gets marked wrong. Redraw until
        // every reading agrees.
        repeat(SEQUENCE_ATTEMPTS) {
            val (terms, answer, hidden) = sequenceCandidate(spec, rng)
            if (hidden != null || nextTermReadings(terms) == setOf(answer)) {
                val shown = terms.mapIndexed { i, t -> if (i == hidden) "?" else t.toString() }
                val text = if (hidden == null) shown.joinToString(", ") + ", ?" else shown.joinToString(", ")
                return Riddle(id, RiddleKind.SEQUENCE, listOf(listOf(RiddleSegment.Text(text))), answer)
            }
        }
        // A plain arithmetic run with a non-zero step fits no other rule.
        val a = rng.intIn(1, spec.sequenceMaxStart); val d = rng.intIn(2, spec.sequenceMaxStep.coerceAtLeast(2))
        val text = (0..3).joinToString(", ") { (a + it * d).toString() } + ", ?"
        return Riddle(id, RiddleKind.SEQUENCE, listOf(listOf(RiddleSegment.Text(text))), a + 4 * d)
    }

    private const val SEQUENCE_ATTEMPTS = 40

    /**
     * Every next term a reasonable player could defend for four shown [terms], one per rule the
     * generator uses. A fair question has exactly one.
     */
    internal fun nextTermReadings(terms: List<Int>): Set<Int> {
        val (a, b, c, d) = terms
        val d1 = b - a; val d2 = c - b; val d3 = d - c
        return buildSet {
            if (d1 == d2 && d2 == d3) add(d + d3) // arithmetic
            if (a != 0 && b % a == 0 && b / a > 1 && c == b * (b / a) && d == c * (b / a)) add(d * (b / a)) // geometric
            if (d2 - d1 == d3 - d2) add(d + d3 + (d3 - d2)) // growing gaps, squares
            if (c == a + b && d == b + c) add(c + d) // Fibonacci
            val k = b - 2 * a
            if (c == 2 * b + k && d == 2 * c + k) add(2 * d + k) // double, then add k
            if (d1 == d3 && d1 != d2) add(d + d2) // zig-zag
            val roots = terms.map { t -> (1..40).firstOrNull { it * it * it == t } }
            if (roots.all { it != null } && roots.zipWithNext().all { (x, y) -> y == x!! + 1 }) {
                val next = roots.last()!! + 1
                add(next * next * next) // cubes
            }
        }
    }

    /** One draw of a sequence: the terms, the value at the hidden slot, and that slot (null = the next term). */
    private fun sequenceCandidate(spec: TierSpec, rng: PuzzleRandom): Triple<List<Int>, Int, Int?> =
        when (rng.nextInt(9)) {
            0 -> { // arithmetic
                val a = rng.intIn(1, spec.sequenceMaxStart); val d = rng.intIn(1, spec.sequenceMaxStep)
                Triple((0..3).map { a + it * d }, a + 4 * d, null)
            }
            1 -> { // geometric
                val a = rng.intIn(1, spec.sequenceMaxStart); val r = rng.intIn(2, spec.sequenceMaxRatio)
                Triple((0..3).map { a * pow(r, it) }, a * pow(r, 4), null)
            }
            2 -> { // increasing differences: +d, +2d, +3d ...
                val a = rng.intIn(1, spec.sequenceMaxStart); val d = rng.intIn(1, spec.sequenceMaxStep)
                val t = mutableListOf(a)
                for (i in 1..3) t += t.last() + i * d
                Triple(t.toList(), t.last() + 4 * d, null)
            }
            3 -> { // squares starting at s
                val s0 = rng.intIn(1, 6)
                Triple((0..3).map { (s0 + it) * (s0 + it) }, (s0 + 4) * (s0 + 4), null)
            }
            4 -> { // Fibonacci-style: each term is the sum of the previous two
                val a = rng.intIn(1, 6); val b = rng.intIn(1, 9)
                val t = mutableListOf(a, b)
                repeat(3) { t += t[t.size - 1] + t[t.size - 2] }
                Triple(t.take(4), t[4], null)
            }
            5 -> { // double, then add k
                val a = rng.intIn(1, 6); val k = rng.intIn(1, 4)
                val t = mutableListOf(a)
                repeat(4) { t += t.last() * 2 + k }
                Triple(t.take(4), t[4], null)
            }
            6 -> { // zig-zag: +x, -y, +x, -y ...
                val y = rng.intIn(1, 5); val x = y + rng.intIn(1, 6); val a = rng.intIn(2 * y, 2 * y + 8)
                val t = mutableListOf(a)
                for (i in 0 until 4) t += if (i % 2 == 0) t.last() + x else t.last() - y
                Triple(t.take(4), t[4], null)
            }
            7 -> { // cubes
                val s0 = rng.intIn(1, 3)
                Triple((0..3).map { (s0 + it) * (s0 + it) * (s0 + it) }, (s0 + 4) * (s0 + 4) * (s0 + 4), null)
            }
            else -> { // arithmetic with a hole in the middle
                val a = rng.intIn(1, spec.sequenceMaxStart); val d = rng.intIn(2, spec.sequenceMaxStep.coerceAtLeast(3))
                val hole = rng.intIn(1, 3)
                Triple((0..4).map { a + it * d }, a + hole * d, hole)
            }
        }

    private fun pow(base: Int, exp: Int): Int {
        var r = 1
        repeat(exp) { r *= base }
        return r
    }
}
